import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Truck, PackageCheck, MapPin, IndianRupee, Fuel, User, Clock, Download, FileText, ShieldAlert } from "lucide-react";

const API_BASE = api.defaults.baseURL;

async function downloadChallanPdf(id, code) {
  try {
    const res = await fetch(`${API_BASE}/exports/challan/${id}/pdf`, { credentials: "include" });
    if (!res.ok) { toast.error(`Download failed (${res.status})`); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(code || "challan").replace(/\//g, "_")}.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error("Download failed", { description: e?.message || String(e) });
  }
}

async function downloadInvoicePdf(id, code, template = "standard") {
  try {
    const res = await fetch(`${API_BASE}/exports/invoice/${id}/pdf?template=${template}`, { credentials: "include" });
    if (!res.ok) { toast.error(`Download failed (${res.status})`); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(code || "invoice").replace(/\//g, "_")}-${template}.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error("Download failed", { description: e?.message || String(e) });
  }
}

function useOrder(id) {
  return useQuery({ queryKey: ["order", id], queryFn: () => api.get(`/orders/${id}`).then((r) => r.data), refetchInterval: 10000 });
}

function StepPill({ done, active, label }) {
  return (
    <div className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${
      done ? "border-status-delivered/60 bg-status-delivered/10 text-status-delivered" :
      active ? "border-primary bg-primary/10 text-primary" :
      "border-border text-muted-foreground"
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${done ? "bg-status-delivered" : active ? "bg-primary" : "bg-muted-foreground"}`} />
      {label}
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: order, isLoading } = useOrder(id);
  const [assignOpen, setAssignOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [startTripOpen, setStartTripOpen] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);

  const approve = useMutation({
    mutationFn: () => api.post(`/orders/${id}/approve`, {}),
    onSuccess: () => { toast.success("Order approved"); qc.invalidateQueries({ queryKey: ["order", id] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/orders/${id}/cancel`),
    onSuccess: () => { toast.success("Cancelled"); qc.invalidateQueries({ queryKey: ["order", id] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  if (isLoading || !order) return <div>Loading...</div>;

  const canApprove = user?.role === "admin" || user?.role === "sales";
  const canDispatch = user?.role === "admin" || user?.role === "dispatcher";
  const canOps = ["admin", "operator", "dispatcher", "driver"].includes(user?.role);

  const steps = ["pending", "approved", "dispatched", "in_transit", "delivered"];
  const currentIdx = steps.indexOf(order.status === "credit_checked" ? "approved" : order.status === "loading" ? "in_transit" : order.status);

  return (
    <div className="space-y-6" data-testid="order-detail">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Order</div>
          <h1 className="font-display text-3xl font-black tracking-tight">{order.code}</h1>
          <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
            <StatusBadge status={order.status} />
            {order.emergency && <span className="rounded bg-status-cancelled/10 px-2 py-0.5 text-xs font-bold uppercase text-status-cancelled">Emergency</span>}
            <span>Created {new Date(order.created_at).toLocaleString()}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canApprove && order.status === "pending" && (
            <Button onClick={() => approve.mutate()} data-testid="btn-approve">Approve & credit check</Button>
          )}
          {canDispatch && ["pending","approved","credit_checked"].includes(order.status) && (
            <Button variant="secondary" onClick={() => setAssignOpen(true)} data-testid="btn-open-assign">Assign bowser · driver</Button>
          )}
          {canOps && order.status === "dispatched" && (
            <Button variant="secondary" onClick={() => setStartTripOpen(true)} data-testid="btn-open-start-trip">Start trip · water check</Button>
          )}
          {canOps && ["in_transit","loading"].includes(order.status) && (
            <Button onClick={() => setDeliverOpen(true)} data-testid="btn-open-deliver">Record delivery</Button>
          )}
          {canApprove && !["delivered","cancelled"].includes(order.status) && (
            <Button variant="outline" onClick={() => cancel.mutate()} data-testid="btn-cancel">Cancel</Button>
          )}
          {order.challan_id && (
            <Button variant="outline" onClick={() => downloadChallanPdf(order.challan_id, order.challan_code || order.code)} data-testid="btn-dl-challan">
              <FileText size={14} className="mr-1" /> Challan PDF
            </Button>
          )}
          {order.invoice_id && (
            <>
              <Button variant="outline" onClick={() => downloadInvoicePdf(order.invoice_id, order.invoice_code || order.code, "standard")} data-testid="btn-dl-invoice-std">
                <Download size={14} className="mr-1" /> Invoice
              </Button>
              <Button variant="outline" onClick={() => downloadInvoicePdf(order.invoice_id, order.invoice_code || order.code, "spreadsheet")} data-testid="btn-dl-invoice-sheet">
                <Download size={14} className="mr-1" /> Invoice (Spreadsheet)
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {steps.map((s, i) => <StepPill key={s} done={i < currentIdx || order.status === "delivered"} active={i === currentIdx} label={s.replace("_", " ")} />)}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Order details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Info icon={User} label="Customer" value={<Link className="hover:text-primary" to={`/app/customers/${order.customer_id}`}>{order.customer_name}</Link>} />
            <Info
              icon={MapPin}
              label="Site"
              value={
                <div className="space-y-1">
                  <div>{order.site_name || "—"}</div>
                  {order.site_lat && order.site_lng && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${order.site_lat},${order.site_lng}&travelmode=driving`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                      data-testid="admin-order-navigate-link"
                    >
                      <MapPin size={10} /> Open in Maps ({order.site_lat.toFixed(4)}, {order.site_lng.toFixed(4)})
                    </a>
                  )}
                </div>
              }
              sub={order.site_address}
            />
            <Info icon={Fuel} label="Quantity" value={`${order.quantity_litres} L`} />
            <Info icon={IndianRupee} label="Rate" value={`₹${order.price_per_litre} / L`} />
            <Info icon={IndianRupee} label="Total" value={`₹${order.total_amount?.toLocaleString("en-IN")}`} highlight />
            <Info icon={Clock} label="Scheduled" value={order.scheduled_at ? new Date(order.scheduled_at).toLocaleString() : "ASAP"} />
            <Info icon={Truck} label="Bowser" value={order.bowser_code || "—"} />
            <Info icon={User} label="Driver" value={order.driver_name || "—"} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Trip data</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Row label="Dispatched" value={order.dispatched_at ? new Date(order.dispatched_at).toLocaleString() : "—"} />
            <Row label="Loading start" value={order.loading_completed_at ? new Date(order.loading_completed_at).toLocaleString() : "—"} />
            <Row label="Opening / Closing meter" value={order.loading_opening_meter ? `${order.loading_opening_meter} → ${order.loading_closing_meter}` : "—"} />
            <Row label="Batch · Seal" value={order.loading_batch ? `${order.loading_batch} · ${order.loading_seal_no}` : "—"} />
            <Row label="Temp / Density" value={order.loading_temp ? `${order.loading_temp}°C · ${order.loading_density} kg/L` : "—"} />
            <Row label="Delivered at" value={order.delivered_at ? new Date(order.delivered_at).toLocaleString() : "—"} />
            <Row label="Delivered qty" value={order.delivered_quantity ? `${order.delivered_quantity} L` : "—"} />
            <Row label="Delivery OTP" value={order.delivery_otp || "—"} mono />
            <OtpOverrideRow orderId={id} order={order} onDone={() => qc.invalidateQueries({ queryKey: ["order", id] })} />
          </CardContent>
        </Card>
      </div>

      {order.notes && <Card><CardContent className="p-4 text-sm text-muted-foreground">Notes: {order.notes}</CardContent></Card>}

      <AssignDialog id={id} open={assignOpen} onClose={() => setAssignOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["order", id] })} />
      <LoadingDialog id={id} open={loadOpen} onClose={() => setLoadOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["order", id] })} />
      <StartTripDialog id={id} open={startTripOpen} onClose={() => setStartTripOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["order", id] })} />
      <DeliveryDialog id={id} order={order} open={deliverOpen} onClose={() => setDeliverOpen(false)} onDone={() => qc.invalidateQueries({ queryKey: ["order", id] })} />
    </div>
  );
}

const Info = ({ icon: Icon, label, value, sub, highlight }) => (
  <div className="rounded-lg border border-border/60 p-3">
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
      <Icon size={12} /> {label}
    </div>
    <div className={`mt-1 font-semibold ${highlight ? "text-primary text-lg" : ""}`}>{value}</div>
    {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
  </div>
);
const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between border-b border-border/40 pb-2 last:border-0">
    <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
    <span className={`text-right ${mono ? "font-mono font-bold text-primary" : ""}`}>{value}</span>
  </div>
);

/** Admin-only manual OTP override.
 * Used when the customer is unreachable at delivery site. Bypasses the
 * start or end OTP check and writes an audit-log entry with the reason.
 */
function OtpOverrideRow({ orderId, order, onDone }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("end");
  const [reason, setReason] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post(`/orders/${orderId}/otp-override`, { otp_kind: kind, reason }),
    onSuccess: () => { toast.success(`${kind === "start" ? "Start" : "End"} OTP manually verified · audit logged`); setOpen(false); setReason(""); onDone?.(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Override failed"),
  });
  const canOverride = user && ["admin", "manager"].includes(user.role);
  const startDone = !!order.start_otp_verified_at;
  const endDone = !!order.otp_verified_at;
  if (!canOverride || (startDone && endDone) || order.status === "delivered") return null;

  return (
    <>
      <div className="rounded-md border border-status-cancelled/30 bg-status-cancelled/5 p-2">
        <div className="flex items-center gap-2 text-xs">
          <ShieldAlert size={14} className="text-status-cancelled" />
          <span className="font-semibold">Manual OTP override</span>
          <span className="text-muted-foreground">— use only if customer unreachable</span>
        </div>
        <button type="button"
          onClick={() => setOpen(true)}
          className="mt-1 text-xs font-bold text-status-cancelled underline"
          data-testid="btn-otp-override">
          Override {startDone ? "END" : (endDone ? "START" : "start / end")} OTP
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="otp-override-dialog">
          <DialogHeader><DialogTitle>Manual OTP override</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-status-cancelled/10 p-2 text-xs">
              This action is <b>audit-logged</b>. It should only be used when the customer is genuinely unreachable at the delivery site.
            </div>
            <div>
              <Label>Which OTP to override?</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="otp-override-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {!startDone && <SelectItem value="start">Start OTP (dispensing authorisation)</SelectItem>}
                  {!endDone && <SelectItem value="end">End OTP (delivery confirmation)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reason <span className="text-status-cancelled">*</span></Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer unreachable at site despite 3 call attempts; supervisor approved via WhatsApp." data-testid="otp-override-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => mut.mutate()}
              disabled={!reason.trim() || mut.isPending}
              data-testid="btn-confirm-otp-override"
              className="bg-status-cancelled hover:bg-status-cancelled/90 text-white"
            >
              {mut.isPending ? "Overriding…" : "Confirm override"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AssignDialog({ id, open, onClose, onDone }) {
  // Pull `order` too so we can filter bowsers by required litres.
  const { data: order } = useQuery({
    queryKey: ["order", id],
    queryFn: () => api.get(`/orders/${id}`).then((r) => r.data),
    enabled: open,
  });
  const requiredLitres = Number(order?.quantity_litres || 0);
  const { data } = useQuery({
    queryKey: ["dispatch-available", requiredLitres],
    queryFn: () => api.get(`/dispatch/available?litres=${requiredLitres}`).then((r) => r.data),
    enabled: open,
  });
  const [bowser, setBowser] = useState("");
  const [driver, setDriver] = useState("");
  const chosenBowser = (data?.bowsers || []).find((b) => b.id === bowser);
  // Bowser is insufficient only if its already-committed + this order exceeds
  // physical stock. The backend applies the same guard authoritatively; this
  // is a UX warning so dispatchers can adjust before submitting.
  const insufficient = chosenBowser && Number(chosenBowser.free_litres ?? chosenBowser.current_stock ?? 0) < requiredLitres;
  const chosenDriver = (data?.drivers || []).find((d) => d.id === driver);

  const submit = async () => {
    if (!bowser || !driver) { toast.error("Select bowser and driver"); return; }
    try { await api.post(`/orders/${id}/dispatch`, { bowser_id: bowser, driver_id: driver }); toast.success("Dispatched"); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assign bowser &amp; driver</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-border/60 bg-muted/40 p-2 text-xs">
            Order needs <b>{requiredLitres} L</b>. You can stack this on a bowser / driver
            that already has active trips — the list shows their current commitments so
            you can decide.
          </div>
          <div className="space-y-2">
            <Label>Bowser</Label>
            <Select value={bowser} onValueChange={setBowser}>
              <SelectTrigger data-testid="select-bowser"><SelectValue placeholder="Pick bowser" /></SelectTrigger>
              <SelectContent>
                {(data?.bowsers || []).length === 0 && (
                  <SelectItem value="__none__" disabled>No bowsers with enough free stock — record a Loading</SelectItem>
                )}
                {(data?.bowsers || []).map((b) => {
                  const committed = Number(b.committed_litres || 0);
                  const free = Number(b.free_litres ?? b.current_stock ?? 0);
                  return (
                    <SelectItem key={b.id} value={b.id}>
                      {b.code} · {b.registration_no || ""} · free {Math.round(free)}L
                      {committed > 0 ? ` (${Math.round(committed)}L already committed)` : ""} / cap {b.capacity_litres}L
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {insufficient && (
              <div className="text-xs text-status-cancelled">
                ⚠ Selected bowser has only {Math.round(chosenBowser.free_litres ?? chosenBowser.current_stock ?? 0)} L free — required {requiredLitres} L. Load it first.
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Driver</Label>
            <Select value={driver} onValueChange={setDriver}>
              <SelectTrigger data-testid="select-driver"><SelectValue placeholder="Pick driver" /></SelectTrigger>
              <SelectContent>
                {(data?.drivers || []).length === 0 && (
                  <SelectItem value="__none_d__" disabled>No active drivers</SelectItem>
                )}
                {(data?.drivers || []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.code} · {d.name} · {d.phone}
                    {d.queue_length ? ` · ${d.queue_length} trip${d.queue_length > 1 ? "s" : ""} in queue` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chosenDriver?.queue_length > 0 && (
              <div className="text-xs text-muted-foreground">
                ℹ {chosenDriver.name} already has {chosenDriver.queue_length} active trip{chosenDriver.queue_length > 1 ? "s" : ""}: {(chosenDriver.pending_orders || []).join(", ")}. This will be added to the queue.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!bowser || !driver || insufficient} data-testid="btn-confirm-dispatch">Dispatch order</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoadingDialog({ id, open, onClose, onDone }) {
  const [form, setForm] = useState({ opening_meter: 10000, closing_meter: 12000, batch: "B-2026-101", temperature: 28.5, density: 0.832, water_test: false, seal_no: "SL-99001", photo_url: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const submit = async () => {
    try { await api.post(`/orders/${id}/loading`, form); toast.success("Loading recorded"); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="loading-dialog">
        <DialogHeader><DialogTitle>Loading advice</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Opening meter</Label><Input type="number" value={form.opening_meter} onChange={set("opening_meter")} data-testid="load-opening" /></div>
          <div><Label>Closing meter</Label><Input type="number" value={form.closing_meter} onChange={set("closing_meter")} data-testid="load-closing" /></div>
          <div><Label>Batch</Label><Input value={form.batch} onChange={set("batch")} data-testid="load-batch" /></div>
          <div><Label>Seal number</Label><Input value={form.seal_no} onChange={set("seal_no")} data-testid="load-seal" /></div>
          <div><Label>Temperature (°C)</Label><Input type="number" step="0.1" value={form.temperature} onChange={set("temperature")} data-testid="load-temp" /></div>
          <div><Label>Density (kg/L)</Label><Input type="number" step="0.001" value={form.density} onChange={set("density")} data-testid="load-density" /></div>
          <div className="col-span-2 flex items-center gap-3 rounded-lg border p-3">
            <Switch checked={form.water_test} onCheckedChange={(v) => setForm({ ...form, water_test: v })} data-testid="load-water" />
            <Label>Water contamination detected?</Label>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="btn-confirm-loading">Record & start trip</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryDialog({ id, order, open, onClose, onDone }) {
  const [form, setForm] = useState({ otp: "", delivered_quantity: order.quantity_litres, meter_reading: 0, temperature: 28.7, density: 0.833, photo_url: "", signature_url: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const submit = async () => {
    try { await api.post(`/orders/${id}/deliver`, form); toast.success("Delivery completed. Invoice generated."); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="delivery-dialog">
        <DialogHeader><DialogTitle>Complete delivery</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Customer OTP {order.delivery_otp && <span className="text-xs text-muted-foreground">(hint: {order.delivery_otp})</span>}</Label>
            <Input value={form.otp} onChange={set("otp")} placeholder="4-digit OTP from customer" data-testid="deliver-otp" /></div>
          <div><Label>Delivered qty (L)</Label><Input type="number" value={form.delivered_quantity} onChange={set("delivered_quantity")} data-testid="deliver-qty" /></div>
          <div><Label>Meter reading</Label><Input type="number" value={form.meter_reading} onChange={set("meter_reading")} data-testid="deliver-meter" /></div>
          <div><Label>Temperature</Label><Input type="number" step="0.1" value={form.temperature} onChange={set("temperature")} data-testid="deliver-temp" /></div>
          <div><Label>Density</Label><Input type="number" step="0.001" value={form.density} onChange={set("density")} data-testid="deliver-density" /></div>
          <div className="col-span-2"><Label>Photo URL (optional)</Label><Input value={form.photo_url} onChange={set("photo_url")} placeholder="https://..." data-testid="deliver-photo" /></div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="btn-confirm-delivery">Complete delivery</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Dispatcher-side "Start trip" — confirms water & quality check, transitions
 * the order from `dispatched` → `in_transit`, and generates the delivery OTP.
 * The driver's UI now waits for this action instead of self-starting.
 */
function StartTripDialog({ id, open, onClose, onDone }) {
  const [waterPass, setWaterPass] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!waterPass) { toast.error("Confirm water check pass to proceed"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${id}/start-trip`, { water_check_pass: true, notes });
      toast.success("Trip started · driver notified");
      onDone(); onClose();
      setNotes(""); setWaterPass(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start trip");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="start-trip-dialog">
        <DialogHeader><DialogTitle>Start trip · water & quality check</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-xs">
            Confirm the bowser has passed the water contamination check before releasing
            it. The driver will only get the &quot;on route&quot; screen after you start the trip.
          </div>
          <label className="flex items-center gap-3 rounded-md border border-border/60 p-3">
            <input
              type="checkbox"
              checked={waterPass}
              onChange={(e) => setWaterPass(e.target.checked)}
              data-testid="start-trip-water-pass"
            />
            <span className="text-sm">Water check passed — no contamination detected</span>
          </label>
          <div>
            <Label>Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. density verified 0.833 kg/L; seal SL-99001 intact"
              rows={2}
              data-testid="start-trip-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!waterPass || busy} data-testid="btn-confirm-start-trip">
            {busy ? "Starting…" : "Start trip"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

